/**
 * traceModel — pure selectors + the DETAILED node/edge projection over a
 * build/asset-gen trace (capture v2). The full-page explorer renders THIS
 * model with React Flow; the compact studio panel keeps its own coarse
 * projection (same steps, one source of truth, two projections — the
 * trace.ts doctrine). Everything here is layout-free and side-effect-free so
 * it unit-tests without a DOM.
 */
import type { AssetGenTraceView, BuildTraceView } from '../services/types.js';

export type TraceStep = BuildTraceView['steps'][number];

// ── step selectors ────────────────────────────────────────────────────────────

export const llmSteps = (steps: TraceStep[]): TraceStep[] => steps.filter((s) => s.type === 'llm');

export const assetSteps = (steps: TraceStep[]): TraceStep[] =>
  steps.filter((s) => s.type === 'asset-step');

export const assetPhases = (steps: TraceStep[]): TraceStep[] =>
  steps.filter((s) => s.type === 'asset-phase');

/** Distinct asset names, in first-appearance (plan) order. */
export function assetNames(steps: TraceStep[]): string[] {
  const names: string[] = [];
  for (const s of assetSteps(steps)) {
    const name = String(s.asset ?? '');
    if (name && !names.includes(name)) names.push(name);
  }
  return names;
}

/** One asset's full ordered chain (planned → … → uploaded/banked). */
export const assetChain = (steps: TraceStep[], name: string): TraceStep[] =>
  assetSteps(steps).filter((s) => s.asset === name);

/** The terminal outcome of one asset's chain, for node toning. */
export function assetOutcome(chain: TraceStep[]): 'uploaded' | 'banked' | 'failed' | 'pending' {
  for (let i = chain.length - 1; i >= 0; i--) {
    const st = String(chain[i].step);
    if (st === 'uploaded') return 'uploaded';
    if (st === 'banked' || st === 'upload-failed') return 'banked';
    if (st === 'no-image' && chain.length - 1 === i) return 'failed';
  }
  return 'pending';
}

// ── the explorer graph (semantic, layout-free) ───────────────────────────────

export interface ExplorerNode {
  id: string;
  /** Node renderer family — drives the small-box look + the inspector body. */
  kind:
    | 'inputs'
    | 'llm'
    | 'gates'
    | 'diagnosis'
    | 'quality'
    | 'asset-pipeline'
    | 'asset'
    | 'bind'
    | 'publish'
    | 'post';
  /** Eyebrow label ("LLM · claude-opus-4-8", "GATES", …). */
  eyebrow: string;
  title: string;
  /** Epoch ms of the node's anchor step (absent for synthesized nodes). */
  at?: number;
  tone?: 'ok' | 'bad' | 'accent' | 'muted';
  /** One-line stat rendered inside the small box. */
  stat?: string;
  /** The trace steps this node aggregates — the inspector's raw material. */
  steps: TraceStep[];
  /** Column: 0 = main track, 1 = asset track. */
  lane: 0 | 1;
}

export interface ExplorerEdge {
  from: string;
  to: string;
  label?: string;
  tone?: 'bad' | 'muted';
}

export interface ExplorerGraph {
  nodes: ExplorerNode[];
  edges: ExplorerEdge[];
}

const fmtDur = (ms: number): string => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);
const fmtCost = (usd: number): string => `$${usd.toFixed(usd >= 0.1 ? 2 : 4)}`;

function llmStat(s: TraceStep): string {
  const cost = typeof s.estimatedCostUsd === 'number' ? ` · ${fmtCost(s.estimatedCostUsd)}` : '';
  const dur = typeof s.durationMs === 'number' ? fmtDur(s.durationMs) : '—';
  return `${String(s.inputTokens ?? '?')}→${String(s.outputTokens ?? '?')} tok${cost} · ${dur}`;
}

/**
 * Project a BUILD trace into the detailed explorer graph: every LLM call,
 * gate battery, diagnosis, and ASSET is its own small node; the repair loop
 * and the parallel art lane are edges. Deterministic per trace.
 */
export function buildExplorerGraph(trace: BuildTraceView): ExplorerGraph {
  const steps = trace.steps;
  const nodes: ExplorerNode[] = [];
  const edges: ExplorerEdge[] = [];
  const milestone = (name: string): TraceStep | undefined =>
    steps.find((s) => s.type === 'milestone' && s.name === name);

  const llm = llmSteps(steps);
  const buildLlm = llm.filter((s) => s.task === 'build' || s.task === 'iterate' || s.task === 'repair-fast');
  const editRounds = steps.filter((s) => s.type === 'edit-round');
  const gatesFor = (r: number): TraceStep[] =>
    steps.filter((s) => s.type === 'gate' && s.phase === 'round' && Number(s.round) === r);
  const regate = steps.filter((s) => s.type === 'gate' && s.phase === 're-gate');
  const diagnoses = steps.filter((s) => s.type === 'diagnosis');
  const seeded = milestone('seeded');

  // INPUTS
  nodes.push({
    id: 'inputs',
    kind: 'inputs',
    eyebrow: 'INPUTS',
    title: trace.mode === 'copy' ? 'seed archive' : 'build request',
    tone: 'accent',
    stat: seeded ? `seed: ${String(seeded.from)}` : undefined,
    steps: [...(seeded ? [seeded] : [])],
    lane: 0,
  });
  let prev = 'inputs';

  // AMEND
  const amend = llm.find((s) => s.task === 'amend');
  if (amend) {
    nodes.push({
      id: 'amend',
      kind: 'llm',
      eyebrow: `LLM amend · ${String(amend.model)}`,
      title: 'design registrar',
      at: amend.t,
      stat: llmStat(amend),
      steps: [amend],
      lane: 0,
    });
    edges.push({ from: prev, to: 'amend' });
    prev = 'amend';
  }

  // CODE TRACK: llm-r → gates-r, fail loops forward into the next round
  const rounds = [...new Set(editRounds.map((s) => Number(s.round)))].sort((a, b) => a - b);
  const effRounds = rounds.length > 0 ? rounds : buildLlm.map((_, i) => i + 1);
  effRounds.forEach((r, i) => {
    const call = buildLlm[i];
    const er = editRounds.find((s) => Number(s.round) === r);
    const files = Array.isArray(er?.filesWritten) ? (er!.filesWritten as string[]) : [];
    const gates = gatesFor(r);
    const checkpoint = steps.find((s) => s.type === 'candidate-checkpoint' && Number(s.round) === r);
    const gatesOk = gates.length > 0 && gates.every((g) => g.ok === true);
    if (call) {
      nodes.push({
        id: `llm-${r}`,
        kind: 'llm',
        eyebrow: `LLM ${String(call.task)} · ${String(call.model)}`,
        title: `edit round ${r}`,
        at: call.t,
        tone: er && er.kind !== 'applied' ? 'bad' : undefined,
        stat: llmStat(call),
        steps: [call, ...(er ? [er] : [])],
        lane: 0,
      });
      edges.push({ from: prev, to: `llm-${r}` });
      prev = `llm-${r}`;
    }
    const diag = diagnoses.find((s) => Number(s.round) === r);
    if (diag) {
      nodes.push({
        id: `diag-${r}`,
        kind: 'diagnosis',
        eyebrow: 'DIAGNOSIS',
        title: `agentic probe · round ${r}`,
        at: diag.t,
        tone: diag.producedFix ? 'ok' : 'muted',
        stat: `${String(diag.probesRun)} probe(s) · ${diag.producedFix ? 'fix produced' : 'no fix'}`,
        steps: [diag],
        lane: 0,
      });
      edges.push({ from: prev, to: `diag-${r}`, tone: 'muted' });
      prev = `diag-${r}`;
    }
    if (gates.length > 0) {
      nodes.push({
        id: `gates-${r}`,
        kind: 'gates',
        eyebrow: 'GATES',
        title: `battery · round ${r}`,
        at: gates[0].t,
        tone: gatesOk ? 'ok' : 'bad',
        stat: `${gates.filter((g) => g.ok === true).length}/${gates.length} pass`,
        steps: [...gates, ...(checkpoint ? [checkpoint] : [])],
        lane: 0,
      });
      edges.push({ from: prev, to: `gates-${r}`, label: files.length ? `${files.length} files` : undefined });
      prev = `gates-${r}`;
      if (!gatesOk && i + 1 < effRounds.length) {
        edges.push({ from: `gates-${r}`, to: `llm-${effRounds[i + 1]}`, label: 'repair', tone: 'bad' });
      }
    }
  });

  // copy-build battery (round 0)
  const copyGates = steps.filter(
    (s) => s.type === 'gate' && s.phase === 'round' && Number(s.round) === 0,
  );
  if (trace.mode === 'copy' && copyGates.length > 0) {
    const okAll = copyGates.every((g) => g.ok === true);
    nodes.push({
      id: 'copy-gates',
      kind: 'gates',
      eyebrow: 'GATES',
      title: 'battery (republish)',
      at: copyGates[0].t,
      tone: okAll ? 'ok' : 'bad',
      stat: `${copyGates.filter((g) => g.ok === true).length}/${copyGates.length} pass`,
      steps: copyGates,
      lane: 0,
    });
    edges.push({ from: prev, to: 'copy-gates' });
    prev = 'copy-gates';
  }

  // QUALITY (safety review + critic + quality passes)
  const review = llm.find((s) => s.task === 'review');
  const critic = llm.find((s) => s.task === 'critic');
  const safeties = steps.filter((s) => s.type === 'safety-review');
  const qualityPasses = steps.filter((s) => s.type === 'quality-pass');
  if (review || critic || qualityPasses.length > 0) {
    const safe = safeties.length > 0 ? safeties.every((s) => s.safe) : true;
    const score = qualityPasses[0]?.score;
    nodes.push({
      id: 'quality',
      kind: 'quality',
      eyebrow: `LLM review/critic${critic ? ` · ${String(critic.model)}` : ''}`,
      title: 'safety · quality',
      at: (review ?? critic)?.t,
      tone: safe ? 'ok' : 'bad',
      stat: score !== undefined && score !== null ? `score ${String(score)}` : safe ? 'safe' : 'UNSAFE',
      steps: [...(review ? [review] : []), ...(critic ? [critic] : []), ...safeties, ...qualityPasses],
      lane: 0,
    });
    edges.push({ from: prev, to: 'quality' });
    prev = 'quality';
  }

  // ASSET TRACK: the pipeline node + one node PER ASSET
  const phases = assetPhases(steps);
  const names = assetNames(steps);
  const outcomes = steps.filter((s) => s.type === 'asset-outcome');
  const budget = milestone('asset-budget-expired');
  if (phases.length > 0 || names.length > 0 || outcomes.length > 0) {
    const genPhase = phases.find((p) => p.phase === 'generate');
    nodes.push({
      id: 'assets',
      kind: 'asset-pipeline',
      eyebrow: 'ASSET PIPELINE',
      title: 'plan → generate → judge',
      at: genPhase?.t,
      tone: budget ? 'bad' : 'accent',
      stat: budget
        ? `budget expired — ${String(budget.banked ?? '?')} banked`
        : `${names.length} planned · ${phases.length} phases`,
      steps: [...phases, ...(budget ? [budget] : []), ...outcomes],
      lane: 1,
    });
    edges.push({ from: 'inputs', to: 'assets', label: 'parallel' });
    for (const name of names) {
      const chain = assetChain(steps, name);
      const outcome = assetOutcome(chain);
      nodes.push({
        id: `asset:${name}`,
        kind: 'asset',
        eyebrow: String(chain[0]?.kind ?? 'asset').toUpperCase(),
        title: name,
        at: chain[0]?.t,
        tone: outcome === 'uploaded' ? 'ok' : outcome === 'pending' ? 'muted' : 'bad',
        stat: outcome,
        steps: chain,
        lane: 1,
      });
      edges.push({ from: 'assets', to: `asset:${name}` });
    }
  }

  // BIND
  const inject = steps.find((s) => s.type === 'asset-inject');
  const unbound = steps.find((s) => s.type === 'asset-inject-unbound');
  if (inject || regate.length > 0) {
    nodes.push({
      id: 'bind',
      kind: 'bind',
      eyebrow: 'BIND ASSETS',
      title: 'inject bindings · re-gate',
      at: inject?.t,
      tone: inject?.injected ? 'ok' : inject ? 'bad' : undefined,
      stat: inject?.injected ? 'bindings injected' : inject ? 'reverted' : undefined,
      steps: [...(inject ? [inject] : []), ...(unbound ? [unbound] : []), ...regate],
      lane: 0,
    });
    edges.push({ from: prev, to: 'bind' });
    for (const name of names) edges.push({ from: `asset:${name}`, to: 'bind', tone: 'muted' });
    prev = 'bind';
  }

  // PUBLISH
  const artifacts = steps.filter((s) => s.type === 'artifact' && s.kind !== 'trace');
  const errors = steps.filter((s) => s.type === 'error');
  nodes.push({
    id: 'publish',
    kind: 'publish',
    eyebrow: 'PUBLISH',
    title: `→ ${trace.status}`,
    at: artifacts[0]?.t,
    tone: trace.status === 'live' ? 'ok' : trace.status === 'failed' ? 'bad' : undefined,
    stat: artifacts.length > 0 ? `${artifacts.length} artifact(s)` : undefined,
    steps: [...artifacts, ...errors],
    lane: 0,
  });
  edges.push({ from: prev, to: 'publish' });
  if (names.length === 0 && (phases.length > 0 || outcomes.length > 0)) {
    edges.push({ from: 'assets', to: 'publish', tone: 'muted' });
  }

  // POST
  const artCompletion = milestone('art-completion-enqueued');
  const cover = milestone('cover-art');
  const suggest = llm.find((s) => s.task === 'suggest');
  if (artCompletion || cover || suggest) {
    nodes.push({
      id: 'post',
      kind: 'post',
      eyebrow: suggest ? `POST · ${String(suggest.model)}` : 'POST-PUBLISH',
      title: 'after go-live',
      at: (artCompletion ?? cover ?? suggest)?.t,
      stat: artCompletion ? `${String(artCompletion.jobs ?? '?')} art job(s)` : undefined,
      steps: [
        ...(artCompletion ? [artCompletion] : []),
        ...(cover ? [cover] : []),
        ...(suggest ? [suggest] : []),
      ],
      lane: 0,
    });
    edges.push({ from: 'publish', to: 'post' });
  }

  return { nodes, edges };
}

/**
 * Project a studio async-generation JOB trace into the same graph shape (the
 * shared sub-flowchart: planned → phases → per-variant outcomes).
 */
export function jobExplorerGraph(trace: AssetGenTraceView): ExplorerGraph {
  const steps = trace.steps as TraceStep[];
  const nodes: ExplorerNode[] = [];
  const edges: ExplorerEdge[] = [];
  const names = assetNames(steps);
  const name = names[0] ?? 'asset';
  const chain = assetChain(steps, name);
  const planned = chain.find((s) => s.step === 'planned');

  nodes.push({
    id: 'inputs',
    kind: 'inputs',
    eyebrow: 'REQUEST',
    title: name,
    tone: 'accent',
    at: planned?.t,
    stat: planned ? `${String(planned.kind)} · ${String(planned.variants ?? '?')} variant(s)` : undefined,
    steps: planned ? [planned] : [],
    lane: 0,
  });
  let prev = 'inputs';
  for (const phase of assetPhases(steps)) {
    const id = `phase:${String(phase.phase)}`;
    nodes.push({
      id,
      kind: 'asset-pipeline',
      eyebrow: 'PHASE',
      title: String(phase.phase),
      at: phase.t,
      tone: phase.ok === false ? 'bad' : 'ok',
      stat: typeof phase.durationMs === 'number' ? fmtDur(phase.durationMs) : undefined,
      steps: [phase],
      lane: 0,
    });
    edges.push({ from: prev, to: id });
    prev = id;
  }
  const variantSteps = chain.filter((s) => s.step === 'uploaded' || s.step === 'no-image');
  for (const v of variantSteps) {
    const slug = String(v.slug ?? '?');
    const id = `variant:${slug}`;
    nodes.push({
      id,
      kind: 'asset',
      eyebrow: 'VARIANT',
      title: slug,
      at: v.t,
      tone: v.step === 'uploaded' ? 'ok' : 'bad',
      stat: v.step === 'uploaded' ? 'uploaded' : 'no usable image',
      steps: [v],
      lane: 1,
    });
    edges.push({ from: prev, to: id });
  }
  return { nodes, edges };
}

// ── Gantt spans ───────────────────────────────────────────────────────────────

export interface GanttSpan {
  /** The explorer node this span highlights when clicked. */
  nodeId: string;
  label: string;
  start: number;
  end: number;
  tone: 'ok' | 'bad' | 'accent' | 'muted';
}

/** Duration bars for the timeline lane. LLM/gate durations END at their step
 *  timestamp (steps record on completion); zero-duration steps get a 1ms tick. */
export function ganttSpans(graph: ExplorerGraph, startedAt: number, finishedAt: number | null): GanttSpan[] {
  const spans: GanttSpan[] = [];
  for (const n of graph.nodes) {
    let start: number | null = null;
    let end: number | null = null;
    for (const s of n.steps) {
      const dur = typeof s.durationMs === 'number' ? s.durationMs : 0;
      const s0 = s.t - dur;
      if (start === null || s0 < start) start = s0;
      if (end === null || s.t > end) end = s.t;
    }
    if (start === null || end === null) {
      if (n.at === undefined) continue;
      start = n.at;
      end = n.at;
    }
    spans.push({
      nodeId: n.id,
      label: n.title,
      start: Math.max(start, startedAt),
      end: Math.min(Math.max(end, start + 1), finishedAt ?? end + 1),
      tone: n.tone === 'bad' ? 'bad' : n.tone === 'ok' ? 'ok' : n.tone === 'accent' ? 'accent' : 'muted',
    });
  }
  return spans.sort((a, b) => a.start - b.start);
}
